'use client';

import { useCallback } from 'react';
import type { ListBlock, ListItem } from '@impact/types';
import { Download } from 'lucide-react';

interface ListBlockRendererProps {
  block: ListBlock;
  editing?: boolean;
}

function getFileExtension(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const ext = pathname.split('.').pop();
    return ext && ext.length <= 5 ? `.${ext}` : '';
  } catch {
    return '';
  }
}

function DownloadLink({ item, editing }: { item: ListItem; editing?: boolean }) {
  const label = item.content || item.fileName || 'Download';

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (editing || !item.fileUrl) return;

      const res = await fetch(item.fileUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = label + getFileExtension(item.fileUrl);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    },
    [editing, item.fileUrl, label],
  );

  return (
    <a
      href={editing ? '#' : item.fileUrl}
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 text-primary hover:underline cursor-pointer"
    >
      <Download className="h-4 w-4 flex-shrink-0" />
      {label}
    </a>
  );
}

/**
 * Renders a list block with text or download items
 */
export function ListBlockRenderer({ block, editing }: ListBlockRendererProps) {
  const { title, listType, items } = block.data;
  const ListTag = listType === 'ol' ? 'ol' : 'ul';
  const listClassName =
    listType === 'ol'
      ? 'list-decimal list-inside space-y-3'
      : listType === 'none'
        ? 'list-none space-y-3'
        : 'list-disc list-inside space-y-3';

  return (
    <div className="my-12">
      {title && (
        <h2
          className="text-2xl md:text-3xl font-bold text-foreground mb-4"
          {...(editing ? { 'data-editable': 'data.title' } : {})}
        >
          {title}
        </h2>
      )}
      <ListTag className={listClassName}>
        {items.map((item, i) => (
          <li key={item.id} {...(editing ? { 'data-block-item': String(i) } : {})}>
            {item.type === 'text' ? (
              editing ? (
                <span
                  className="inline-block min-w-[4rem] text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Click_to_edit']"
                  data-editable={`data.items[${i}].content`}
                >
                  {item.content}
                </span>
              ) : (
                <span
                  className="prose prose-sm max-w-none inline text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              )
            ) : (
              <DownloadLink item={item} editing={editing} />
            )}
          </li>
        ))}
      </ListTag>
    </div>
  );
}
